/**
 * Centralized configuration for Secret Book Price frontend.
 *
 * This module provides:
 * - API endpoint configuration
 * - Feature flags
 * - Application settings
 * - Environment-aware configuration
 *
 * Target: 50-100 LOC
 * Module: #26 (First frontend module)
 * Dependencies: None (foundational)
 */

// =============================================================================
// Types & Interfaces
// =============================================================================

export interface ApiEndpoints {
  batchLookup: string;
  scanCount: string;
  redirect: string;
  health: string;
}

export interface RetryConfig {
  max: number;
  delay: number; // ms
  backoff: number;
}

export interface ApiConfig {
  baseUrl: string;
  version: string;
  endpoints: ApiEndpoints;
  timeout: number; // ms
  retries: RetryConfig;
}

export interface FeatureFlags {
  enableCameraScanning: boolean;
  enableManualEntry: boolean;
  enableCaching: boolean;
  enableAnalytics: boolean;
  enableDebugMode: boolean;
  enableToastNotifications: boolean;
}

export interface TierLimits {
  guest: number; // 5 scans/day
  bronze: number; // 25 scans/day
  silver: number; // 100 scans/day
  gold: number; // -1 = unlimited
}

export type UserTier = "guest" | "bronze" | "silver" | "gold";

export interface ScanLimit {
  /** @deprecated Use tierLimits instead */
  anonymous: number;
  /** @deprecated Use tierLimits instead */
  authenticated: number; // -1 = unlimited
  /** Tier-based daily limits */
  tierLimits: TierLimits;
}

export interface CacheConfig {
  ttl: number; // seconds
  enabled: boolean;
}

export interface UIConfig {
  theme: "light" | "dark";
  locale: string;
}

export interface AppConfig {
  name: string;
  version: string;
  scanLimit: ScanLimit;
  cache: CacheConfig;
  ui: UIConfig;
}

export interface Config {
  api: ApiConfig;
  features: FeatureFlags;
  app: AppConfig;
  environment: "development" | "production" | "test";
}

// =============================================================================
// Environment Detection
// =============================================================================

const getEnvironment = (): "development" | "production" | "test" => {
  // Vite uses import.meta.env.MODE
  const env = import.meta.env?.MODE || "development";
  if (env === "production") return "production";
  if (env === "test") return "test";
  return "development";
};

export const isDevelopment = (): boolean => getEnvironment() === "development";
export const isProduction = (): boolean => getEnvironment() === "production";
export const isTest = (): boolean => getEnvironment() === "test";

// =============================================================================
// API Configuration
// =============================================================================

const getApiBaseUrl = (): string => {
  // Try Vite env var (VITE_API_BASE_URL)
  if (import.meta.env?.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Default based on environment
  if (isProduction()) {
    return "https://api.secretbookprice.com";
  }

  return "http://localhost:8000";
};

const apiConfig: ApiConfig = {
  baseUrl: getApiBaseUrl(),
  version: "v1",
  endpoints: {
    batchLookup: "/api/v1/prices/batch",
    scanCount: "/api/v1/users/scan-count",
    redirect: "/api/v1/redirect",
    health: "/api/v1/health",
  },
  timeout: 30000, // 30 seconds
  retries: {
    max: 3,
    delay: 1000, // 1 second
    backoff: 2, // exponential backoff multiplier
  },
};

// =============================================================================
// Feature Flags
// =============================================================================

const featureFlags: FeatureFlags = {
  enableCameraScanning: true,
  enableManualEntry: true,
  enableCaching: true,
  enableAnalytics: isProduction(),
  enableDebugMode: isDevelopment(),
  enableToastNotifications: true,
};

// =============================================================================
// Application Configuration
// =============================================================================

const appConfig: AppConfig = {
  name: "Secret Book Price",
  version: "1.0.0",
  scanLimit: {
    // Legacy fields (deprecated)
    anonymous: 5, // Now maps to guest tier
    authenticated: -1, // unlimited
    // New tier-based limits (daily reset at UTC midnight)
    tierLimits: {
      guest: 5, // Anonymous users
      bronze: 25, // Device-token auth
      silver: 100, // Email auth
      gold: -1, // Premium - unlimited
    },
  },
  cache: {
    ttl: 60, // 60 seconds
    enabled: true,
  },
  ui: {
    theme: "light",
    locale: "en-US",
  },
};

// =============================================================================
// Main Configuration Export
// =============================================================================

export const config: Config = {
  api: apiConfig,
  features: featureFlags,
  app: appConfig,
  environment: getEnvironment(),
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get the full URL for a specific endpoint.
 * @param endpoint - The endpoint key from ApiEndpoints
 * @returns Full URL string
 */
export const getEndpointUrl = (endpoint: keyof ApiEndpoints): string => {
  return `${config.api.baseUrl}${config.api.endpoints[endpoint]}`;
};

/**
 * Build a vendor redirect URL.
 * @param vendor - Vendor identifier
 * @param isbn - Book ISBN
 * @returns Full redirect URL
 */
export const buildRedirectUrl = (vendor: string, isbn: string): string => {
  return `${config.api.baseUrl}${config.api.endpoints.redirect}/${vendor}/${isbn}`;
};

/**
 * Build a bulk vendor redirect URL for adding multiple books to cart at once.
 * @param vendor - Vendor identifier
 * @param isbns - Array of ISBNs to add to cart
 * @returns Full bulk redirect URL
 */
export const buildBulkRedirectUrl = (
  vendor: string,
  isbns: string[],
): string => {
  const isbnList = isbns.join(",");
  return `${config.api.baseUrl}${config.api.endpoints.redirect}/${vendor}/bulk?isbns=${encodeURIComponent(isbnList)}`;
};

/**
 * Check if a specific feature is enabled.
 * @param feature - Feature flag key
 * @returns True if feature is enabled
 */
export const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
  return config.features[feature];
};

/**
 * Get the scan limit for a user based on authentication status.
 * @param isAuthenticated - Whether the user is authenticated
 * @returns Scan limit (-1 for unlimited)
 * @deprecated Use getTierLimit instead
 */
export const getScanLimit = (isAuthenticated: boolean): number => {
  return isAuthenticated
    ? config.app.scanLimit.authenticated
    : config.app.scanLimit.anonymous;
};

/**
 * Get the daily scan limit for a specific tier.
 * @param tier - User tier (guest, bronze, silver, gold)
 * @returns Daily scan limit (-1 for unlimited)
 */
export const getTierLimit = (tier: UserTier): number => {
  return config.app.scanLimit.tierLimits[tier];
};

/**
 * Check if a tier has unlimited scans.
 * @param tier - User tier
 * @returns True if unlimited
 */
export const isTierUnlimited = (tier: UserTier): boolean => {
  return getTierLimit(tier) === -1;
};

/**
 * Get display name for a tier.
 * @param tier - User tier
 * @returns Display name
 */
export const getTierDisplayName = (tier: UserTier): string => {
  const names: Record<UserTier, string> = {
    guest: "Guest",
    bronze: "Bronze",
    silver: "Silver",
    gold: "Gold",
  };
  return names[tier];
};

// =============================================================================
// Constants Export
// =============================================================================

export const CONSTANTS = {
  /** @deprecated Use TIER_LIMITS instead */
  ANONYMOUS_SCAN_LIMIT: config.app.scanLimit.anonymous,
  CACHE_TTL_SECONDS: config.app.cache.ttl,
  API_TIMEOUT_MS: config.api.timeout,
  MAX_RETRIES: config.api.retries.max,
  /** Tier-based daily limits */
  TIER_LIMITS: config.app.scanLimit.tierLimits,
} as const;

// =============================================================================
// Scanner Configuration (STRICH SDK)
// =============================================================================

/**
 * Scanner symbology types supported by STRICH SDK
 * For ISBN scanning, we primarily use EAN-13
 */
export type BarcodeSymbology =
  | "ean13" // Standard ISBN barcodes (primary)
  | "ean8" // Short EAN barcodes
  | "upca" // UPC-A (North American)
  | "upce" // UPC-E (North American, compressed)
  | "code128" // Code 128 (versatile)
  | "code39" // Code 39
  | "qr"; // QR codes (optional for future use)

export interface ScannerConfig {
  /** STRICH SDK license key from environment */
  licenseKey: string;

  /** Barcode symbologies to detect (fewer = faster scanning) */
  symbologies: BarcodeSymbology[];

  /** Duplicate scan filter interval in milliseconds */
  duplicateInterval: number;

  /** Enable torch/flashlight control (on supported devices) */
  enableTorch: boolean;

  /** Enable audio beep on successful scan */
  enableBeep: boolean;

  /** Enable haptic feedback on successful scan (mobile only) */
  enableHaptic: boolean;

  /** Camera selection preference */
  preferredCamera: "environment" | "user";
}

/**
 * Get STRICH license key from environment
 * Throws error if not configured (required for scanner operation)
 */
const getStrichLicenseKey = (): string => {
  const licenseKey = import.meta.env?.VITE_STRICH_LICENSE_KEY;

  if (!licenseKey || licenseKey === "your_license_key_here") {
    console.warn(
      "STRICH license key not configured. Scanner functionality will be limited. " +
        "Get your license key at https://portal.strich.io/ and set VITE_STRICH_LICENSE_KEY",
    );
    return "";
  }

  return licenseKey;
};

/**
 * Scanner configuration for barcode detection
 * Optimized for ISBN (EAN-13) barcode scanning
 */
export const scannerConfig: ScannerConfig = {
  licenseKey: getStrichLicenseKey(),

  // For ISBN scanning, we focus on EAN-13 (standard ISBN barcode format)
  // Including EAN-8 and UPC-A/E for broader compatibility
  symbologies: ["ean13", "ean8", "upca", "upce"],

  // Prevent duplicate scans of the same barcode within 2.5 seconds
  duplicateInterval: 2500,

  // Enable torch control (flashlight) for low-light scanning
  enableTorch: true,

  // Enable audio feedback on successful scan
  enableBeep: true,

  // Enable haptic feedback on mobile devices
  enableHaptic: true,

  // Prefer rear camera (environment) for barcode scanning
  preferredCamera: "environment",
};

/**
 * Check if scanner is configured and ready to use
 * @returns True if STRICH license key is configured
 */
export const isScannerConfigured = (): boolean => {
  return scannerConfig.licenseKey.length > 0;
};

// Default export
export default config;
